import { useState, useEffect } from "react";
import { useEffectEvent } from "@/shared/lib/use-effect-event";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useStudents, useDeleteStudent, useBlockStudent } from "@/lib/api/students/hooks";
import { useOffices } from "@/lib/api/offices/hooks/use-offices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
} from "@/components/ui/combobox";
import { Trash2, Edit, Search, Ban, BarChart2 } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";
import { StudentEditDialog } from "@/components/students/student-edit-dialog";
import { Student } from "@/lib/api/students/types";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

function StudentsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [user, setUser] = useState<{
    role: string;
    office?: { _id: string; name: string };
  } | null>(null);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>("");
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const handleUser = useEffectEvent(async () => {
    const stored = localStorage.getItem("growing-teacher-user");
    if (stored) {
      const userData = JSON.parse(stored);
      setUser(userData);
      // 일반 선생님은 본인 지점으로 자동 필터링
      if (userData.role !== "admin" && userData.office?._id) {
        setSelectedOfficeId(userData.office._id);
      }
    }
  });

  useEffect(() => {
    handleUser();
  }, []);

  const isAdmin = user?.role === "admin";

  // 관리자용 지점 목록 조회
  const { data: officesData } = useOffices();

  // 필터링할 officeId 결정: 관리자는 선택한 지점, 일반 선생님은 본인 지점
  const filterOfficeId = isAdmin ? selectedOfficeId : user?.office?._id;

  const { data: studentsData, isLoading } = useStudents({
    search,
    page,
    limit: 20,
    officeId: filterOfficeId || undefined,
  });
  const deleteStudent = useDeleteStudent();
  const blockStudent = useBlockStudent();

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDelete = (id: string, name: string) => {
    setAlertDialogState({
      title: "학생 삭제",
      description: `정말 "${name}" 학생을 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteStudent.mutateAsync(id);
          toast.success("삭제되었습니다.");
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const handleBlock = (id: string, name: string) => {
    setAlertDialogState({
      title: "학생 차단",
      description: `정말 "${name}" 학생을 차단하시겠습니까? 차단된 학생은 승인 관리 페이지에서 다시 승인할 수 있습니다.`,
      confirmText: "차단",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await blockStudent.mutateAsync(id);
          toast.success("차단되었습니다.");
        } catch {
          toast.error("차단에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  useLoadingIndicator(isLoading);

  return (
    <PageContainer>
      <Header title="학생 관리" />

      {/* 검색 및 필터 */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* 관리자용 지점 필터 */}
        {isAdmin && officesData && (
          <Combobox
            items={officesData}
            value={officesData.find((o) => o._id === selectedOfficeId) ?? null}
            onValueChange={(office) => {
              setSelectedOfficeId(office?._id ?? "");
              setPage(1);
            }}
            itemToStringLabel={(o) => o.name}
            isItemEqualToValue={(a, b) => a._id === b._id}
          >
            <ComboboxInput placeholder="전체 지점" showClear className="w-[180px]" />
            <ComboboxContent>
              <ComboboxEmpty>결과 없음</ComboboxEmpty>
              <ComboboxList>
                {(office) => (
                  <ComboboxItem key={office._id} value={office}>
                    {office.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )}
        <Input
          placeholder="이름 또는 아이디로 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          wrapperClassName="max-w-sm"
        />
        <Button onClick={handleSearch} variant="outline">
          <Search className="size-4 mr-2" />
          검색
        </Button>
      </div>

      {/* 학생 목록 */}
      <div className="bg-card rounded-lg border">
        {studentsData && studentsData.students.length > 0 ? (
          <>
            {/* 테이블 헤더 */}
            <div className="hidden md:grid grid-cols-6 gap-4 p-4 border-b font-semibold text-sm text-muted-foreground">
              <div>이름</div>
              <div>아이디</div>
              <div>전화번호</div>
              <div>생년월일</div>
              <div>학교</div>
              <div></div>
            </div>

            {/* 테이블 바디 */}
            <div className="divide-y">
              {studentsData.students.map((student) => (
                <div
                  key={student._id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  {/* 데스크톱 뷰 */}
                  <div className="hidden md:grid grid-cols-6 gap-4 items-center">
                    <div className="font-medium">{student.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {student.userId}
                    </div>
                    <div className="text-sm">
                      {formatPhoneNumber(student.phoneNumber)}
                    </div>
                    <div className="text-sm">
                      {formatBirthDate(student.birthDate)}
                    </div>
                    <div className="text-sm">{student.school || "-"}</div>
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          navigate({ to: `/students/${student._id}/statistics` })
                        }
                        title="통계"
                      >
                        <BarChart2 className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingStudent(student)}
                      >
                        <Edit className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleBlock(student._id, student.name)}
                      >
                        <Ban className="size-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleDelete(student._id, student.name)
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* 모바일 뷰 */}
                  <div className="md:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{student.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {student.userId}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            navigate({ to: `/students/${student._id}/statistics` })
                          }
                        >
                          <BarChart2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingStudent(student)}
                        >
                          <Edit className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleBlock(student._id, student.name)}
                        >
                          <Ban className="size-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleDelete(student._id, student.name)
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatPhoneNumber(student.phoneNumber)} ·{" "}
                      {formatBirthDate(student.birthDate)}
                      {student.school && ` · ${student.school}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 페이지네이션 */}
            {studentsData.totalPages > 1 && (
              <div className="flex gap-2 p-4 justify-center border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  이전
                </Button>
                <span className="flex items-center px-4 text-sm">
                  {page} / {studentsData.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === studentsData.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  다음
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            {search ? "검색 결과가 없습니다." : "등록된 학생이 없습니다."}
          </div>
        )}
        </div>

        {/* 수정 다이얼로그 */}
        {editingStudent && (
          <StudentEditDialog
            student={editingStudent}
            open={!!editingStudent}
            onOpenChange={(open) => !open && setEditingStudent(null)}
            schoolOnly={!isAdmin}
          />
        )}
    </PageContainer>
  );
}

function formatPhoneNumber(phone: string): string {
  if (phone.length === 11) {
    return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
  }
  return phone;
}

function formatBirthDate(birthDate: string): string {
  if (birthDate.length === 6) {
    return `${birthDate.slice(0, 2)}.${birthDate.slice(2, 4)}.${birthDate.slice(
      4
    )}`;
  }
  return birthDate;
}

export const Route = createFileRoute("/_content/students/")({
  component: StudentsPage,
});
